import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';

import Modal from '../../../Components/UI/Modal/Modal';
import { addEstimation } from '../../../Store/Actions/actionOffer';
import { isArtisanApproved } from '../../../Utils/artisanStatus';
import firebaseApp from '../../../Firebase/Firebase';
import { offerRef } from '../../../Services/demandOffers';
import { useI18n } from '../../../i18n/I18nProvider';



const EstimationWindow = ({
  showModal,
  handleClick,
  demandId,
  firebase,
  loading,
  postEstimation,
  initialOffer = null,
}) => {
  const { t } = useI18n();
  const assessmentOptions = [
    { value: 'direct_quote', title: t('estimationForm.directTitle'), description: t('estimationForm.directDescription') },
    { value: 'visit_required', title: t('estimationForm.visitTitle'), description: t('estimationForm.visitDescription') },
    { value: 'more_info', title: t('estimationForm.infoTitle'), description: t('estimationForm.infoDescription') },
  ];
  const [message, setMessage] = useState('');
  const [phone, setPhone] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [estimatedBudget, setEstimatedBudget] = useState('');
  const [assessmentMode, setAssessmentMode] = useState('visit_required');
  const [additionalInfoRequest, setAdditionalInfoRequest] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [persistedOffer, setPersistedOffer] = useState(null);
  const opened = showModal === 'open';
  const profile = firebase?.profile || {};
  const userId = firebase?.auth?.uid;
  const approved = isArtisanApproved(profile.registration_status);
  const resolvedOffer = initialOffer || persistedOffer;

  useEffect(() => {
    let active = true;
    if (!opened || !userId || !demandId || initialOffer) {
      setPersistedOffer(initialOffer || null);
      return () => { active = false; };
    }

    offerRef(firebaseApp.firestore(), demandId, userId)
      .get()
      .then((snapshot) => {
        if (!active) return;
        setPersistedOffer(snapshot.exists ? { ...snapshot.data(), offer_id: snapshot.id } : null);
      })
      .catch(() => {
        if (active) setPersistedOffer(null);
      });

    return () => { active = false; };
  }, [opened, userId, demandId, initialOffer]);

  useEffect(() => {
    if (!opened) return;
    setPhone(resolvedOffer?.artisan_phone_number || profile.phone_number || '');
    setMessage(resolvedOffer?.estimation_description || '');
    setVisitDate(resolvedOffer?.visit_date || '');
    setEstimatedBudget(resolvedOffer?.estimated_budget ? String(resolvedOffer.estimated_budget) : '');
    setAssessmentMode(resolvedOffer?.assessment_mode || 'visit_required');
    setAdditionalInfoRequest(resolvedOffer?.additional_info_request || '');
    setError('');
  }, [opened, profile.phone_number, demandId, resolvedOffer]);

  const close = () => {
    if (!submitting) handleClick?.();
  };

  const submit = async () => {
    if (submitting) return;
    if (!userId || !demandId) {
      setError(t('estimationForm.sessionMissing'));
      return;
    }
    if (!approved) {
      setError(t('estimationForm.approvalRequired'));
      return;
    }
    if (message.trim().length < 10) {
      setError(t('estimationForm.messageTooShort'));
      return;
    }
    if (!phone.trim()) {
      setError(t('estimationForm.phoneRequired'));
      return;
    }
    if (assessmentMode === 'visit_required' && !visitDate) {
      setError(t('estimationForm.visitRequired'));
      return;
    }
    if (assessmentMode === 'direct_quote' && Number(estimatedBudget) <= 0) {
      setError(t('estimationForm.budgetRequired'));
      return;
    }
    if (assessmentMode === 'more_info' && additionalInfoRequest.trim().length < 10) {
      setError(t('estimationForm.infoRequired'));
      return;
    }

    setError('');
    setSubmitting(true);
    const success = await postEstimation({
      demandId,
      artisanId: userId,
      artisanFirstName: profile.firstName,
      artisanLastName: profile.lastName,
      artisanEmail: profile.email,
      artisanProfileImage: profile.profile_image,
      descriptionOfEstimation: message.trim(),
      visitDateEstimation: assessmentMode === 'visit_required' ? visitDate : '',
      contactNumber: phone.trim(),
      materialCostEstim: 0,
      wageEstim: 0,
      estimatedBudget: estimatedBudget ? Number(estimatedBudget) : 0,
      assessmentMode,
      additionalInfoRequest: assessmentMode === 'more_info' ? additionalInfoRequest.trim() : '',
    });
    setSubmitting(false);
    if (success) close();
  };

  const selectedOption = assessmentOptions.find((option) => option.value === assessmentMode);

  return (
    <Modal opened={opened} close={close}>
      <Header>
        <div>
          <Eyebrow>{resolvedOffer ? t('estimationForm.editEyebrow') : t('estimationForm.newEyebrow')}</Eyebrow>
          <Title>{resolvedOffer ? t('estimationForm.editTitle') : t('estimationForm.newTitle')}</Title>
          <Subtitle>
            {t('estimationForm.subtitle')}
          </Subtitle>
        </div>
        <CloseButton type="button" aria-label={t('common.close')} onClick={close}>×</CloseButton>
      </Header>

      <ProfileCard>
        <Avatar src={profile.profile_image || '/images/user.svg'} alt="" />
        <div>
          <strong>{[profile.firstName, profile.lastName].filter(Boolean).join(' ') || t('common.professional')}</strong>
          <span>{t('estimationForm.identityHint')}</span>
        </div>
      </ProfileCard>

      <SectionLabel>{t('estimationForm.assessmentQuestion')}</SectionLabel>
      <AssessmentGrid role="radiogroup" aria-label={t('estimationForm.assessmentAria')}>
        {assessmentOptions.map((option) => (
          <AssessmentChoice
            key={option.value}
            type="button"
            role="radio"
            aria-checked={assessmentMode === option.value}
            $selected={assessmentMode === option.value}
            onClick={() => setAssessmentMode(option.value)}
          >
            <ChoiceDot $selected={assessmentMode === option.value} />
            <span><strong>{option.title}</strong><small>{option.description}</small></span>
          </AssessmentChoice>
        ))}
      </AssessmentGrid>

      <Form>
        <Field $wide>
          <label htmlFor="estimation-message">{t('estimationForm.messageLabel')}</label>
          <textarea
            id="estimation-message"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder={t('estimationForm.messagePlaceholder')}
            maxLength={1200}
           
          />
          <Hint>{message.length}/1200</Hint>
        </Field>

        {assessmentMode === 'more_info' && (
          <Field $wide>
            <label htmlFor="additional-information">{t('estimationForm.infoLabel')}</label>
            <textarea
              id="additional-information"
              value={additionalInfoRequest}
              onChange={(event) => setAdditionalInfoRequest(event.target.value)}
              placeholder={t('estimationForm.infoPlaceholder')}
              maxLength={800}
            />
            <Hint>{additionalInfoRequest.length}/800</Hint>
          </Field>
        )}

        <Field>
          <label htmlFor="estimation-phone">{t('estimationForm.phone')}</label>
          <input
            id="estimation-phone"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            type="tel"
            autoComplete="tel"
            placeholder="06 00 00 00 00"
          />
        </Field>

        {assessmentMode === 'visit_required' && (
          <Field>
            <label htmlFor="estimation-date">{t('estimationForm.visitDate')}</label>
            <input
              id="estimation-date"
              value={visitDate}
              onChange={(event) => setVisitDate(event.target.value)}
              type="date"
              min={new Date().toISOString().slice(0, 10)}
            />
          </Field>
        )}

        <Field $wide={assessmentMode !== 'visit_required'}>
          <label htmlFor="estimation-budget">
            {t('estimationForm.budget')} ({assessmentMode === 'direct_quote' ? t('estimationForm.required') : t('estimationForm.optional')})
          </label>
          <input
            id="estimation-budget"
            value={estimatedBudget}
            onChange={(event) => setEstimatedBudget(event.target.value)}
            type="number"
            inputMode="decimal"
            min="0"
            step="1"
            placeholder="Ex. 450"
          />
          <Hint $alignStart>
            {assessmentMode === 'direct_quote'
              ? t('estimationForm.directBudgetHint')
              : t('estimationForm.budgetHint')}
          </Hint>
        </Field>
      </Form>

      {!approved && (
        <ApprovalWarning>
          {t('estimationForm.approvalWarning')}
        </ApprovalWarning>
      )}
      <Notice>
        <strong>{selectedOption?.title}.</strong>{' '}
        {t('estimationForm.notice')}
      </Notice>
      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

      <Actions>
        <SecondaryButton type="button" onClick={close} disabled={submitting || loading}>{t('common.cancel')}</SecondaryButton>
        <PrimaryButton type="button" onClick={submit} disabled={submitting || loading || !approved}>
          {submitting ? t('common.sending') : resolvedOffer ? t('estimationForm.saveChanges') : t('estimationForm.sendProposal')}
        </PrimaryButton>
      </Actions>
    </Modal>
  );
};

const Header = styled.header`display:flex;align-items:flex-start;justify-content:space-between;gap:1rem;margin-bottom:1.2rem;`;
const Eyebrow = styled.div`margin-bottom:.35rem;color:#a88a5d;font-size:.76rem;font-weight:900;letter-spacing:.12em;text-transform:uppercase;`;
const Title = styled.h2`margin:0;color:#111;font-size:clamp(1.3rem,4vw,1.85rem);`;
const Subtitle = styled.p`margin:.4rem 0 0;color:#726b61;line-height:1.5;`;
const CloseButton = styled.button`width:2.5rem;height:2.5rem;flex:0 0 auto;border:0;border-radius:50%;background:#f1ece5;color:#111;font:inherit;font-size:1.6rem;cursor:pointer;`;
const ProfileCard = styled.div`
  display:flex;align-items:center;gap:.8rem;padding:.9rem;border:1px solid #e5e1da;border-radius:.9rem;background:#faf8f4;
  div{display:grid;gap:.15rem} span{color:#77726b;font-size:.82rem}
`;
const Avatar = styled.img`width:3.1rem;height:3.1rem;border-radius:50%;object-fit:cover;background:#efede8;`;
const SectionLabel = styled.h3`margin:1.15rem 0 .65rem;font-size:1rem;`;
const AssessmentGrid = styled.div`display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:.7rem;@media(max-width:760px){grid-template-columns:1fr;}`;
const AssessmentChoice = styled.button`
  min-width:0;display:flex;align-items:flex-start;gap:.65rem;padding:.9rem;border:1px solid ${({ $selected }) => ($selected ? '#111' : '#ddd3c4')};border-radius:.9rem;
  background:${({ $selected }) => ($selected ? '#f7f1e7' : '#fff')};color:#111;font:inherit;text-align:left;cursor:pointer;
  span{display:grid;gap:.25rem;min-width:0} strong{font-size:.9rem} small{color:#726b61;line-height:1.4}
`;
const ChoiceDot = styled.i`width:1rem;height:1rem;flex:0 0 auto;margin-top:.12rem;border:3px solid #fff;border-radius:50%;background:${({ $selected }) => ($selected ? '#111' : '#d8d0c5')};box-shadow:0 0 0 1px ${({ $selected }) => ($selected ? '#111' : '#c9c0b4')};`;
const Form = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:.9rem;margin-top:1rem;@media(max-width:580px){grid-template-columns:1fr;}`;
const Field = styled.div`
  grid-column:${({ $wide }) => ($wide ? '1 / -1' : 'auto')};display:grid;gap:.4rem;min-width:0;
  label{color:#222;font-weight:800} input,textarea{width:100%;min-width:0;padding:.8rem;border:1px solid #ddd3c4;border-radius:.8rem;color:#111;background:#fff;font:inherit}
  textarea{min-height:7rem;resize:vertical}
`;
const Hint = styled.span`justify-self:${({ $alignStart }) => ($alignStart ? 'start' : 'end')};color:#7a8790;font-size:.76rem;`;
const ApprovalWarning = styled.p`margin:1rem 0 0;padding:.85rem .95rem;border:1px solid #ead29b;border-radius:.8rem;background:#fff8e7;color:#6f5116;line-height:1.5;`;
const Notice = styled.p`margin:1rem 0 0;padding:.85rem .95rem;border-radius:.8rem;background:#f4ede1;color:#725a37;line-height:1.5;`;
const ErrorMessage = styled.p`margin:.8rem 0 0;color:#a52e27;font-size:.88rem;`;
const Actions = styled.div`display:flex;justify-content:flex-end;gap:.7rem;margin-top:1.25rem;@media(max-width:520px){flex-direction:column-reverse;}`;
const BaseButton = styled.button`min-height:2.8rem;padding:.72rem 1.05rem;border-radius:999px;font:inherit;font-weight:800;cursor:pointer;&:disabled{opacity:.55;cursor:wait}`;
const PrimaryButton = styled(BaseButton)`border:1px solid #c0a375;background:#c0a375;color:#111;`;
const SecondaryButton = styled(BaseButton)`border:1px solid #e7dfd2;background:#fff;color:#111;`;

const mapStateToProps = ({ firebase, demands }) => ({ firebase, loading: demands?.loading });
const mapDispatchToProps = (dispatch) => ({ postEstimation: (payload) => dispatch(addEstimation(payload)) });

export default connect(mapStateToProps, mapDispatchToProps)(EstimationWindow);
